/**
 * Utility functions for Audit Logs
 */

import { 
  TimeRangeOption,
  AuditLogFilters,
  DashboardAggregations,
} from "./types";
import { AUDIT_LOGS_API_CONFIG } from "./apiConfig";

/**
 * Get date range based on time range option
 */
export const getDateRangeByOption = (
  timeRange: TimeRangeOption
): { start_date: string; end_date: string } => {
  const endDate = new Date();
  const startDate = new Date();

  switch (timeRange) {
    case "last_hour":
      startDate.setHours(startDate.getHours() - 1);
      break;
    case "last_24_hours":
      startDate.setDate(startDate.getDate() - 1);
      break;
    case "last_7_days":
      startDate.setDate(startDate.getDate() - 7);
      break;
    case "last_30_days":
      startDate.setDate(startDate.getDate() - 30);
      break;
    case "custom":
      // Custom range should be handled separately
      break;
  }

  return {
    start_date: startDate.toISOString(),
    end_date: endDate.toISOString(),
  };
};

/**
 * Format date to YYYY-MM-DD format for API
 */
export const formatDateForAPI = (date: Date | string): string => {
  if (typeof date === "string") {
    return new Date(date).toISOString().split("T")[0];
  }
  return date.toISOString().split("T")[0];
};

/**
 * Format date for display DD/MM/YYYY
 */
export const formatDateForDisplay = (date: string | Date): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const year = dateObj.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Format datetime for display
 */
export const formatDateTimeForDisplay = (datetime: string): string => {
  const date = new Date(datetime);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Build query parameters for API calls
 */
export const buildFilterParams = (filters: AuditLogFilters): Record<string, any> => {
  const params: Record<string, any> = {};

  if (filters.page) params.page = filters.page;
  if (filters.per_page) params.per_page = filters.per_page;
  if (filters.time_range) params.time_range = filters.time_range;
  if (filters.start_date) params.start_date = filters.start_date;
  if (filters.end_date) params.end_date = filters.end_date;
  if (filters.success !== undefined) params.success = filters.success;
  if (filters.method) params.method = filters.method;
  if (filters.source) params.source = filters.source;
  if (filters.email) params.email = filters.email;
  if (filters.device_id) params.device_id = filters.device_id;
  if (filters.search) params.search = filters.search;
  if (filters.interval) params.interval = filters.interval;

  return params;
};

/**
 * Time range label mapping
 */
export const TIME_RANGE_LABELS: Record<TimeRangeOption, string> = {
  last_hour: "Terakhir 1 Jam",
  last_24_hours: "Terakhir 24 Jam",
  last_7_days: "Terakhir 7 Hari",
  last_30_days: "Terakhir 30 Hari",
  custom: "Rentang Kustom",
};

/**
 * Get time range description for display
 */
export const getTimeRangeDescription = (
  timeRange: TimeRangeOption,
  startDate?: string,
  endDate?: string
): string => {
  if (timeRange === "custom" && startDate && endDate) {
    return `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(endDate)}`;
  }
  return TIME_RANGE_LABELS[timeRange];
};

/**
 * Parse dashboard aggregations data
 */
export const parseDashboardData = (data: DashboardAggregations) => {
  return {
    ...data,
    stats: {
      ...data.stats,
      start_date_formatted: formatDateTimeForDisplay(data.stats.start_date),
      end_date_formatted: formatDateTimeForDisplay(data.stats.end_date),
    },
  };
};

/**
 * Export filename generator
 */
export const generateExportFilename = (
  format: "csv" | "excel",
  timeRange: TimeRangeOption
): string => {
  const timestamp = new Date().toISOString().split("T")[0];
  const ext = format === "csv" ? "csv" : "xlsx";
  return `audit_log_${timeRange}_${timestamp}.${ext}`;
};
